import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  DateTimeInput,
  Form,
  NumberInput,
  ReferenceInput,
  SelectInput,
  minValue,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Market } from '../dataProvider';
import { SLOT_CHOICES } from './placementState';

/**
 * Placement create. Gated placements:write (editor and up): a placement is
 * internal merchandising with no money attached, so booking one confers no value
 * and needs no finer capability (D-placements).
 *
 * MARKET IS EXPLICIT and one-per-placement (D-placements): a GLOBAL admin chooses
 * uk/us deliberately; a single-region admin has market PRE-SET and LOCKED to their
 * own market and cannot create outside their scope (enforced in the submit handler,
 * not just the form). The merchant is chosen from the merchants catalogue (the
 * BRAND), the slot from the fixed enum, plus a weight and a window. There is NO
 * active flag: a new placement is live or scheduled purely by its window.
 *
 * A create produces an action='create' audit entry automatically through the
 * wrapper; there is no audit code here.
 */

const regionToMarket = (region: string): Market =>
  region.toLowerCase() as Market;

interface PlacementCreateValues {
  merchantId: number;
  slot: string;
  market: string;
  weight: number;
  startsAt: string;
  endsAt: string | null;
}

const toIso = (v: string): string => new Date(v).toISOString();

const PlacementCreateForm = () => {
  const { accessibleRegions } = useRegion();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const locked = accessibleRegions.length === 1;
  const lockedMarket = locked ? regionToMarket(accessibleRegions[0]) : '';

  const marketChoices = accessibleRegions.map((r) => ({
    id: regionToMarket(r),
    name: r,
  }));

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as PlacementCreateValues;
    const market = locked ? lockedMarket : values.market;

    // Region guard — cannot book a placement outside the admin's scope, even if
    // the form were bypassed.
    if (!accessibleRegions.includes(market.toUpperCase() as AccessibleRegion)) {
      notify('You cannot create a placement outside your region', {
        type: 'error',
      });
      return;
    }

    try {
      const { data } = await dataProvider.create('placements', {
        data: {
          merchantId: Number(values.merchantId),
          slot: values.slot,
          market,
          weight: Number(values.weight),
          startsAt: toIso(values.startsAt),
          // Blank end = open-ended; the window stays live until ended.
          endsAt:
            values.endsAt == null || values.endsAt === ''
              ? null
              : toIso(values.endsAt),
          createdAt: new Date().toISOString(),
        },
      });
      notify('Placement created', { type: 'success' });
      redirect('list', 'placements', data.id);
    } catch {
      notify('Could not create the placement', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New placement
      </Typography>
      <Form
        defaultValues={{ market: lockedMarket, weight: 1 }}
        onSubmit={onSubmit}
      >
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <ReferenceInput source="merchantId" reference="merchants">
              <SelectInput
                label="Merchant"
                optionText="name"
                validate={required('Merchant is required')}
                fullWidth
              />
            </ReferenceInput>
            <SelectInput
              source="slot"
              label="Slot"
              choices={SLOT_CHOICES}
              validate={required('Slot is required')}
            />
            {locked ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Market
                </Typography>
                <Typography variant="body1">
                  {lockedMarket.toUpperCase()}
                </Typography>
              </Box>
            ) : (
              <SelectInput
                source="market"
                label="Market"
                choices={marketChoices}
                validate={required('Market is required')}
              />
            )}
            <NumberInput
              source="weight"
              label="Weight (share of voice)"
              validate={[required('Weight is required'), minValue(0)]}
            />
            <DateTimeInput
              source="startsAt"
              label="Starts at"
              validate={required('Start is required')}
            />
            <DateTimeInput
              source="endsAt"
              label="Ends at (blank = open-ended)"
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button type="button" onClick={() => redirect('list', 'placements')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `placements` Resource's create. */
export const PlacementCreate = () => (
  <RequireAccess capability="placements:write">
    <PlacementCreateForm />
  </RequireAccess>
);
